import { useEffect, useState } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAccountPlan } from '../../hooks/useAccountUsage';
import { trackEvent, currentLocationTag } from '../../lib/analytics';
import { useFlag } from '../../hooks/useFeatureFlags';

/**
 * SessionStorage key recording that the User dismissed the banner for this
 * browser session. Deliberately session-scoped (not localStorage): the no-plan
 * state blocks every AI feature, so the reminder should return next session
 * while the plan is still absent.
 */
export const NO_PLAN_BANNER_DISMISSED_KEY = 'no_plan_banner_dismissed';

/**
 * NoPlanBanner (task 466) — ambient signal rendered from AppLayout while the
 * account has no plan. Role-aware copy: the account owner gets a "Choose a
 * plan" CTA to the Billing page; a non-owner is directed to their account
 * owner (plan selection is an owner concern — the BillingPage `canManageBilling`
 * precedent).
 *
 * The plan comes from `GET /api/accounts/usage` (task 676 — previously the JWT
 * `plan` claim), so the banner clears on the next refetch after checkout
 * rather than waiting on a token refresh. It stays hidden until the plan is
 * actually known, so a slow or failed request never flashes the banner at an
 * account that has a plan.
 */
export default function NoPlanBanner() {
  const { user } = useAuthContext();
  const { plan, isResolved } = useAccountPlan();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(NO_PLAN_BANNER_DISMISSED_KEY) === '1'
  );

  const hasNoPlan = Boolean(user) && isResolved && !plan;
  // Billing affordances are gated by innovate.fe.billing.page, whose
  // role-owner cohort is the owner check (task 692). The role claim is no
  // longer read here: evaluation is uniform and, as a permission-type flag,
  // fails closed during an Unleash outage.
  const canManageBilling = useFlag('innovate.fe.billing.page');
  // Task 994: the ambient pick-a-plan reminders — this banner, the PlanBadge
  // "No plan" state and the dashboard onboarding plan step — are switchable
  // per environment. Off hides the reminder only; the refusal explanations
  // (ChoosePlanPrompt after a 402, the pre-flight disabled states) are
  // deliberately not gated by it, so a planless User never gets a silent
  // failure. Folded into `visible` rather than an early return so the hooks
  // below still run unconditionally, and so the impression event stays silent
  // while the reminder is off.
  const noticesEnabled = useFlag('innovate.fe.plan.no-plan-notices');
  const visible = noticesEnabled && hasNoPlan && !dismissed;

  // When a plan appears, drop the session dismissal so a future no-plan state
  // (e.g. subscription removed) re-signals instead of staying silently hidden.
  useEffect(() => {
    if (plan) {
      sessionStorage.removeItem(NO_PLAN_BANNER_DISMISSED_KEY);
      setDismissed(false);
    }
  }, [plan]);

  // Impression event — once per appearance, not per render.
  useEffect(() => {
    if (visible) {
      trackEvent('no_plan_banner_shown', {
        location: currentLocationTag(),
        is_owner: canManageBilling,
      });
    }
  }, [visible, canManageBilling]);

  if (!visible) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(NO_PLAN_BANNER_DISMISSED_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      role="status"
      data-testid="no-plan-banner"
      className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3">
        <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
        <p className="flex-1 text-sm text-amber-900 dark:text-amber-200">
          {canManageBilling ? (
            <>
              Your account doesn't have a plan yet — AI features are disabled
              until you choose one.{' '}
              {/* Plain anchor (not router Link): keeps the banner free of
                  router coupling, same reasoning as UpgradePrompt's links. */}
              <a
                href="/settings/subscription"
                data-testid="no-plan-banner-cta"
                onClick={() =>
                  trackEvent('no_plan_banner_cta_clicked', {
                    location: currentLocationTag(),
                  })
                }
                className="font-semibold text-amber-800 dark:text-amber-300 underline hover:text-amber-900 dark:hover:text-amber-200"
              >
                Choose a plan
              </a>
            </>
          ) : (
            <>
              Your account doesn't have a plan yet — ask your account owner to
              choose a plan.
            </>
          )}
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="no-plan-banner-dismiss"
          aria-label="Dismiss"
          className="flex-shrink-0 text-amber-500 hover:text-amber-700 dark:hover:text-amber-300"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
